// RandomPlantSection.jsx
//  09/19/2k22

// Packages, Dependencies, and Shared Libraries
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { pickRandomShowablePlant } from "../DatabaseOperations";

// React Components
import PlantImage from "../Sprite/PlantImage";

// Images
import roomSunny from "../Images/Window/roomSunny.png";

/*******************************************************************************
 * @func RandomPlantSection (React Component)
 * Fetches a random real plant from the database and shows it off sitting in
 * the window scene from the app, with a button to visit its bio page. Only
 * showable plants are featured: full sprite art, and unowned or owned by a
 * public user (see pickRandomShowablePlant).
 *
 * @param onLoadCallback (function): The callback that should fire when this
 * sprite has loaded. It's used to tell the parent component that the sprite is
 * done loading and ready to be shown
 * @return JSX Element
 *
 * @notes None
 *
 ******************************************************************************/
const RandomPlantSection = ({ onLoadCallback }) => {
  // Hooks
  const [plantID, setPlantID] = useState("");
  const [species, setSpecies] = useState(null);
  const [plant, setPlant] = useState(null);
  const navigate = useNavigate();

  // This fires upon render and picks a random showable plant
  useEffect(() => {
    let isMounted = true;

    pickRandomShowablePlant().then(
      (result) => {
        if (!isMounted) return;
        setPlantID(result.plantID);
        setPlant(result.plant);
        setSpecies(result.species);
      },
      () => {} // No showable plant found — the card just doesn't render
    );

    return () => {
      isMounted = false;
    };
  }, []); // End useEffect

  if (!plant || !species) {
    return null;
  }

  const customName = plant.name && plant.name.trim();
  const displayName = customName || species.commonName;

  return (
    <div className="animate-fade-in bg-global-accent rounded-3xl shadow-lift border-4 border-white overflow-hidden w-full max-w-[360px] mx-auto text-center">
      {/* The plant sitting in the app's window scene */}
      <div className="relative">
        <img
          src={roomSunny}
          alt=""
          className="w-full aspect-[5/4] object-cover"
          style={{ objectPosition: "50% 32%" }}
        />
        <div className="absolute top-3 inset-x-0 flex justify-center">
          <span className="font-header text-leaf-green-dark text-lg -rotate-2 bg-white/85 px-4 py-0.5 rounded-full shadow-sm">
            Meet a real Leafling!
          </span>
        </div>
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "97%",
            width: "42%",
            transform: "translate(-50%, -100%)",
          }}
        >
          <PlantImage
            plant={plant}
            species={species}
            onLoadCallback={onLoadCallback}
            width="100%"
          />
        </div>
      </div>
      <div className="p-5 pt-3 font-global">
        <span className="block text-2xl font-extrabold text-leaf-green-dark leading-tight">
          {displayName}
        </span>
        <span className="block text-sm text-leaf-brown-soft mb-3">
          {customName ? species.commonName : " "}
        </span>
        <button
          className="button w-full"
          onClick={() => navigate("/plant/" + plantID)}
        >
          {`Say hi to ${displayName} →`}
        </button>
      </div>
    </div>
  );
}; // End RandomPlantSection

export default RandomPlantSection;
